import React, { useRef, useMemo, useCallback, useState } from 'react'
import Map, { FullscreenControl, GeolocateControl, NavigationControl, ScaleControl } from 'react-map-gl/maplibre';
import { Source, Layer } from 'react-map-gl';
import { range } from 'd3-array';
import { scaleQuantile } from 'd3-scale';
import bbox from '@turf/bbox';
import { SimpleLoading } from '..';
import { usePositionedViewport } from '../../../hooks';
import CPs from '../../../assets/data/mexico-cp'
import GendersLegend, { GENDERS } from './genders-legend';
import { useTranslation } from "react-i18next";

const fillLayerPercentile = {
    type: 'fill',
    paint: {
        'fill-outline-color': '#0040c8',
        'fill-opacity': ["case", ["==", ["get", "percentile"], null], 0, 0.7],
        'fill-color': {
            property: 'percentile',
            stops: [
                [0, '#D7F4DF'],
                [1, '#7AD3A2'],
                [2, '#36AA9D'],
                [3, '#2B7D96'],
                [4, '#2B508C'],
                [5, '#322B68'],
                [6, '#22152E'],
                [7, '#0B0506']
            ]
        },
    }
};

const fillLayerGender = {
    type: 'fill',
    paint: {
        'fill-outline-color': '#0040c8',
        'fill-opacity': ["case", ["==", ["get", "mainGender"], null], 0, 0.7],
        'fill-color': [
            "match",
            ["string", ["get", "mainGender"]],
            "Femenino", "#B4375D",
            "Masculino", "#0B7FA9",
            "Otro", "#F0C829",
            '#AAAAAA'
        ]
    }
};

function updatePercentiles(mapData, featureCollection, scale, selectedGender) {
    const { features } = featureCollection;
    return {
        type: 'FeatureCollection',
        features: features.map(f => {
            const value = mapData.find(item => item.postalCode.toString() === f.properties.d_cp);

            const properties = {
                ...f.properties,
                value: value && value.data[selectedGender] ? value.data[selectedGender] : null,
                percentile: value && value.data[selectedGender] ? scale(value.data[selectedGender]) : null
            };
            return { ...f, properties };
        })
    };
}

function updateMaxGender(mapData, featureCollection) {
    const { features } = featureCollection;

    return {
        type: 'FeatureCollection',
        features: features.map(f => {
            const value = mapData.find(item => item.postalCode.toString() === f.properties.d_cp);
            let highestValue = 0;
            let highestKey = null;

            if (value && value.data) {
                for (const key in value.data) {
                    if (Object.hasOwnProperty.call(value.data, key)) {
                        if (value.data[key] > highestValue) {
                            highestValue = value.data[key];
                            highestKey = key;
                        }
                    }
                }
            }

            let properties = {
                ...f.properties,
                mainGender: highestKey,
            };
            if (value) properties = { ...properties, ...value.data };
            return { ...f, properties };
        })
    };
}

function GenderChoroplethMap(props) {
    const { isLoading, data } = props;
    const { t } = useTranslation('clients-dashboard', { keyPrefix: 'reportsScreen' });
    const { t: tCommon } = useTranslation('common');

    // const [CPs, setCPs] = useState(null);
    // useEffect(() => {
    //     import('../../../../assets/data/mexico-cp').then(data => {
    //         setCPs(data.default);
    //     });
    // }, []);

    const mapRef = useRef();
    const viewport = usePositionedViewport();
    const [hoverInfo, setHoverInfo] = useState(null);
    const [selectedGender, setSelectedGender] = useState()

    const scale = useMemo(() => {
        if (!data || data.length === 0) return null;
        return scaleQuantile().domain(data.map(item => item.avg)).range(range(8))
    }, [data]);

    const sources = useMemo(() => {
        if (!data || data.length === 0 || (!scale && !selectedGender) || !CPs) return null;

        if (selectedGender) {
            // If a gender is selected, the more clients there are in an area, the darker it is filled
            return Object.keys(CPs).map(key => {
                const geojsonData = updatePercentiles(data, CPs[key], scale, selectedGender);
                return (
                    <Source key={`${key}-${selectedGender}`} type="geojson" data={geojsonData}>
                        <Layer id={key} {...fillLayerPercentile} />
                    </Source>
                )
            })
        } else {
            // If no gender is selected, the areas will be colored according to gender with the highest value
            return Object.keys(CPs).map(key => {
                const geojsonData = updateMaxGender(data, CPs[key])
                return (
                    <Source key={key} type="geojson" data={geojsonData}>
                        <Layer id={key} {...fillLayerGender} />
                    </Source>
                )
            })
        }
    }, [data, scale, selectedGender])

    const onClick = useCallback((event) => {
        if (mapRef.current && event.features && event.features.length > 0) {
            const feature = event.features[0];
            if (feature) {
                const [minLng, minLat, maxLng, maxLat] = bbox(feature);

                mapRef.current.fitBounds(
                    [
                        [minLng, minLat],
                        [maxLng, maxLat]
                    ],
                    { padding: 40, duration: 1000 }
                );
            }
        }
    }, [])

    const onHover = useCallback((event) => {
        const {
            features,
            point: { x, y }
        } = event;
        const hoveredFeature = features && features[0];
        setHoverInfo(hoveredFeature ? { feature: hoveredFeature, x, y } : null);
    }, []);

    const onClickGender = (gender) => setSelectedGender(prev => prev === gender ? null : gender);

    if (isLoading || !viewport || !CPs) return <SimpleLoading />

    return (
        <div className='dashboardView-genderChoropleth'>
            <GendersLegend onClick={onClickGender} selectedGenderKey={selectedGender} />

            <Map
                ref={mapRef}
                reuseMaps
                initialViewState={viewport}
                style={{ height: '100%', width: '100%' }}
                mapStyle="https://api.maptiler.com/maps/basic-v2/style.json?key=zQZLhSau046UB7IrjeYK"
                interactiveLayerIds={Object.keys(CPs)}
                onClick={onClick}
                onMouseMove={onHover}
            >
                <GeolocateControl position="top-left" />
                <FullscreenControl position="top-left" />
                <NavigationControl position="top-left" />
                <ScaleControl />

                {sources}

                {hoverInfo && (
                    <div className="dashboardMap-tooltip" style={{ left: hoverInfo.x, top: hoverInfo.y }}>
                        <ul>
                            <li><strong>{t("zipcode")}</strong>: {hoverInfo.feature.properties.d_cp}</li>
                            {
                                selectedGender
                                    ? <li><strong>{t("amount")}</strong>: {hoverInfo.feature.properties.value ?? 0}</li>
                                    : <>
                                        <li><strong>{t("mainGender")}</strong>: {hoverInfo.feature.properties.mainGender ?? "N/D"}</li>
                                        {
                                            hoverInfo.feature.properties.mainGender &&
                                            <>{
                                                GENDERS.map(gender => (
                                                    <li key={gender}><strong>{tCommon(gender)}</strong>: {hoverInfo.feature.properties[gender] ?? 0}</li>
                                                ))
                                            }</>
                                        }
                                    </>
                            }
                        </ul>
                    </div>
                )}
            </Map>
        </div>
    )
}

export default GenderChoroplethMap